/**
 * Welcome Message Utilities
 * Provides language-specific welcome messages for chat agents
 */

/**
 * Get welcome message based on agent name and language
 * @param {string} agentName - Name of the agent
 * @param {string} language - Language setting of the agent
 * @returns {string} Localized welcome message
 */
export const getWelcomeMessage = (agentName, language) => {
  const welcomeMessages = {
    English: `Hello! I'm ${agentName}. How can I help you today?`,
    Spanish: `¡Hola! Soy ${agentName}. ¿Cómo puedo ayudarte hoy?`,
    Hindi: `नमस्ते! मैं ${agentName} हूँ। आज मैं आपकी कैसे मदद कर सकता हूँ?`,
    Bengali: `নমস্কার! আমি ${agentName}। আজ আমি আপনাকে কীভাবে সাহায্য করতে পারি?`,
    Telugu: `నమస్కారం! నేను ${agentName}. ఈరోజు నేను మీకు ఎలా సహాయం చేయగలను?`,
    Tamil: `வணக்கம்! நான் ${agentName}. இன்று நான் உங்களுக்கு எப்படி உதவ முடியும்?`,
    Marathi: `नमस्कार! मी ${agentName} आहे. आज मी तुम्हाला कशी मदत करू शकतो?`,
    Thai: `สวัสดี! ฉันคือ${agentName} วันนี้ฉันจะช่วยคุณได้อย่างไร?`,
    Burmese: `မင်္ဂလာပါ! ကျွန်တော်က ${agentName} ပါ။ ဒီနေ့ ဘယ်လိုကူညီရမလဲ?`,
    Mandarin: `你好！我是${agentName}。今天我能为您做些什么？`,
    Chinese: `你好！我是${agentName}。今天我能为您做些什么？`,
    'Bahasa Malay': `Helo! Saya ${agentName}. Bagaimana saya boleh membantu anda hari ini?`,
    Japanese: `こんにちは！私は${agentName}です。今日はどのようにお手伝いできますか？`,
    'Bahasa Indonesia': `Halo! Saya ${agentName}. Bagaimana saya bisa membantu Anda hari ini?`,
    Lao: `ສະບາຍດີ! ຂ້ອຍແມ່ນ ${agentName}. ມື້ນີ້ຂ້ອຍສາມາດຊ່ວຍທ່ານໄດ້ແນວໃດ?`,
    'Tagalog (Filipino)': `Kumusta! Ako si ${agentName}. Paano kita matutulungan ngayon?`,
    Multilingual: `Hello! I'm ${agentName}. How can I help you today?`,
    Polish: `Cześć! Jestem ${agentName}. Jak mogę Ci dziś pomóc?`,
    Ukrainian: `Привіт! Я ${agentName}. Чим я можу вам сьогодні допомогти?`,
  };
  
  return welcomeMessages[language] || welcomeMessages.English;
};

/**
 * Get welcome message for voice agents (slightly different format)
 * @param {string} agentName - Name of the agent
 * @param {string} language - Language setting of the agent
 * @returns {string} Localized welcome message for voice agents
 */
export const getVoiceWelcomeMessage = (agentName, language) => {
  const voiceWelcomeMessages = {
    English: `Hello! I'm ${agentName}. How can I help you today?`,
    Spanish: `¡Hola! Soy ${agentName}. ¿Cómo puedo ayudarte hoy?`,
    Hindi: `नमस्ते! मैं ${agentName} हूँ। आज मैं आपकी कैसे मदद कर सकता हूँ?`,
    Bengali: `নমস্কার! আমি ${agentName}। আজ আমি আপনাকে কীভাবে সাহায্য করতে পারি?`,
    Telugu: `నమస్కారం! నేను ${agentName}. ఈరోజు నేను మీకు ఎలా సహాయం చేయగలను?`,
    Tamil: `வணக்கம்! நான் ${agentName}. இன்று நான் உங்களுக்கு எப்படி உதவ முடியும்?`,
    Marathi: `नमस्कार! मी ${agentName} आहे. आज मी तुम्हाला कशी मदत करू शकतो?`,
    Thai: `สวัสดี! ฉันคือ${agentName} วันนี้ฉันจะช่วยคุณได้อย่างไร?`,
    Burmese: `မင်္ဂလာပါ! ကျွန်တော်က ${agentName} ပါ။ ဒီနေ့ ဘယ်လိုကူညီရမလဲ?`,
    Mandarin: `你好！我是${agentName}。今天我能为您做些什么？`,
    Chinese: `你好！我是${agentName}。今天我能为您做些什么？`,
    'Bahasa Malay': `Helo! Saya ${agentName}. Bagaimana saya boleh membantu anda hari ini?`,
    Japanese: `こんにちは！私は${agentName}です。今日はどのようにお手伝いできますか？`,
    'Bahasa Indonesia': `Halo! Saya ${agentName}. Bagaimana saya bisa membantu Anda hari ini?`,
    Lao: `ສະບາຍດີ! ຂ້ອຍແມ່ນ ${agentName}. ມື້ນີ້ຂ້ອຍສາມາດຊ່ວຍທ່ານໄດ້ແນວໃດ?`,
    'Tagalog (Filipino)': `Kumusta! Ako si ${agentName}. Paano kita matutulungan ngayon?`,
    Multilingual: `Hello! I'm ${agentName}. How can I help you today?`,
    Polish: `Cześć! Jestem ${agentName}. Jak mogę Ci dziś pomóc?`,
    Ukrainian: `Привіт! Я ${agentName}. Чим я можу вам сьогодні допомогти?`,
  };
  
  return voiceWelcomeMessages[language] || voiceWelcomeMessages.English;
};

/**
 * Get simple welcome message based on language (without agent name)
 * Matches the format "Hello! How can I help you today?"
 * @param {string} language - Language setting of the agent
 * @returns {string} Localized welcome message
 */
export const getSimpleWelcomeMessage = (language) => {
  const welcomeMessages = {
    English: 'Hello! How can I help you today?',
    Spanish: '¡Hola! ¿Cómo puedo ayudarte hoy?',
    Hindi: 'नमस्ते! आज मैं आपकी कैसे मदद कर सकता हूँ?',
    Bengali: 'নমস্কার! আজ আমি আপনাকে কীভাবে সাহায্য করতে পারি?',
    Telugu: 'నమస్కారం! ఈరోజు నేను మీకు ఎలా సహాయం చేయగలను?',
    Tamil: 'வணக்கம்! இன்று நான் உங்களுக்கு எப்படி உதவ முடியும்?',
    Marathi: 'नमस्कार! आज मी तुम्हाला कशी मदत करू शकतो?',
    Thai: 'สวัสดี! วันนี้ฉันจะช่วยคุณได้อย่างไร?',
    Burmese: 'မင်္ဂလာပါ! ဒီနေ့ ဘယ်လိုကူညီရမလဲ?',
    Mandarin: '你好！今天我能为您做些什么？',
    Chinese: '你好！今天我能为您做些什么？',
    'Bahasa Malay': 'Helo! Bagaimana saya boleh membantu anda hari ini?',
    Japanese: 'こんにちは！今日はどのようにお手伝いできますか？',
    'Bahasa Indonesia': 'Halo! Bagaimana saya bisa membantu Anda hari ini?',
    Lao: 'ສະບາຍດີ! ມື້ນີ້ຂ້ອຍສາມາດຊ່ວຍທ່ານໄດ້ແນວໃດ?',
    'Tagalog (Filipino)': 'Kumusta! Paano kita matutulungan ngayon?',
    Multilingual: 'Hello! How can I help you today?',
    Polish: 'Cześć! Jak mogę Ci dziś pomóc?',
    Ukrainian: 'Привіт! Чим я можу вам сьогодні допомогти?',
  };
  
  return welcomeMessages[language] || welcomeMessages.English;
};
